import type { Instance } from "mobx-state-tree";
import { types, flow, getRoot, cast } from "mobx-state-tree";
import * as repo from "./repository";
import { Pipeline } from "./models";
import type { PipelineRow, PipelineSnapshotIn } from "./models";
import type { TablesInsert, TablesUpdate } from "@/types/supabase-helpers";
import type { AdminRootAccess } from "../rootAccess";

export const PipelinesStore = types
  .model("PipelinesStore", {
    pipelines: types.array(Pipeline),
    isLoading: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
  })
  .views((self) => ({
    get pipelinesList() {
      return self.pipelines.slice();
    },
    getById(id: number) {
      return self.pipelines.find((pipeline) => pipeline.id === id);
    },
    getBySlug(slug: string) {
      return self.pipelines.find((pipeline) => pipeline.slug === slug);
    },
  }))
  .actions((self) => ({
    loadPipelines: flow(function* loadPipelines() {
      self.isLoading = true;
      self.error = null;

      try {
        const data: PipelineRow[] = yield repo.listPipelines();
        self.pipelines.clear();
        data.forEach((pipeline) => {
          self.pipelines.push(pipeline as PipelineSnapshotIn);
        });
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      } finally {
        self.isLoading = false;
      }
    }),

    createPipeline: flow(function* createPipeline(pipeline: TablesInsert<"pipelines">) {
      self.error = null;
      try {
        const newPipeline: PipelineRow = yield repo.createPipeline(pipeline);
        self.pipelines.push(newPipeline as PipelineSnapshotIn);
        return newPipeline;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    updatePipeline: flow(function* updatePipeline(id: number, updates: TablesUpdate<"pipelines">) {
      self.error = null;
      try {
        const updatedPipeline: PipelineRow = yield repo.updatePipeline(id, updates);
        const index = self.pipelines.findIndex((pipeline) => pipeline.id === id);
        if (index !== -1) {
          self.pipelines[index] = cast(updatedPipeline as PipelineSnapshotIn);
        }
        return updatedPipeline;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    deletePipeline: flow(function* deletePipeline(id: number) {
      self.error = null;
      try {
        yield repo.deletePipeline(id);
        const index = self.pipelines.findIndex((pipeline) => pipeline.id === id);
        if (index !== -1) {
          self.pipelines.splice(index, 1);
        }
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    // M2M Relationship Methods

    loadPipelineCategories: flow(function* loadPipelineCategories(pipelineId: number) {
      self.error = null;
      try {
        const categoryIds: number[] = yield repo.getPipelineCategories(pipelineId);
        return categoryIds;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    setPipelineCategories: flow(function* setPipelineCategories(
      pipelineId: number,
      categoryIds: number[]
    ) {
      self.error = null;
      try {
        const root = getRoot<AdminRootAccess>(self);
        const userProfileId = root.authStore.userProfileId;
        if (!userProfileId) {
          throw new Error("User must be authenticated to update pipeline relationships");
        }
        yield repo.setPipelineCategories(pipelineId, categoryIds, userProfileId);
        // Invalidate global M2M cache so other components refresh
        root.invalidateM2MCache();
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    clear() {
      self.pipelines.clear();
      self.isLoading = false;
      self.error = null;
    },
  }));

export type PipelinesStoreInstance = Instance<typeof PipelinesStore>;
